import { z } from "zod";
import { SymbolRefSchema } from "./environment-view.js";
import { IllustrationMetadataSchema, RasterRegistrationSchema, VisualProfileSchema } from "./illustration.js";
/**
 * THE ASSET CONTRACT (ADR 0009; phase brief section 3). One pack.json per
 * asset pack under environments/packs/<pack>/, validated as build ERRORS by
 * `pnpm assets:validate`. The contract — not the code — is the artist/
 * re-licensing handoff: a replacement symbol with the same viewBox, the same
 * four line-weight tokens, the same data-part groups, and a green validator
 * is a complete replacement, with zero renderer changes (replaceability is a
 * requirement: licensed blocks WILL be swapped later).
 *
 * Units: 1 unit = 1 cm at 1:50. Standard figure 165 cm. Bays 600 cm,
 * floor-to-floor 390 cm. viewBox equals the symbol's own tight bounding box,
 * no padding. Line weights are PRESENTATION ATTRIBUTES on elements
 * (fill="none" stroke="currentColor" stroke-width="1.8") — never document
 * CSS, which cannot cross the <use>/<symbol> shadow boundary.
 */
/** The four line-weight tokens. Any other stroke-width in a symbol is a validation error. */
export const LINE_WEIGHTS = { cut: 3.4, obj: 1.8, det: 1.0, ghost: 0.8 };
/**
 * ATTENTION AND DEPTH (ADR 0010). Symbols ship with their own weight
 * hierarchy; the COMPOSER re-tones them by where they stand in the story:
 * - FOCUS: everything in a room that is part of the action (the room's
 *   projected weight is not ghost, the room is cut by the plane) draws
 *   every stroke at ONE weight, FOCUS_STROKE, in full ink — the figures and
 *   the furniture read as drawn with the same pen, above the shell's
 *   object lines and below its cut lines. Chosen by eye at 0.8 pt on the
 *   1:50 sheet; 1.2 pt lost the detail.
 * - BEHIND: a room seen through an opening (depth "behind") draws in
 *   BEHIND_PAINT at BEHIND_OPACITY — depth as tone, never as line weight.
 * - GHOST rooms keep the ghost opacity: background, not part of the story.
 * These are composer transforms of the shipped markup, so the asset
 * contract (four tokens, presentation attributes) is untouched.
 */
export const FOCUS_STROKE = 1.4;
/**
 * The behind tone is an OPAQUE paint — a neutral mid grey since the Ink
 * Stroke direction (2026-09-17), at the luminance of the rose it replaced
 * (#B69F9E), so depth reads as before. Opaque so a behind symbol's paper
 * silhouette can hide what stands further back (the courtyard tree over the
 * core). A group opacity could never do that: it lets everything underneath
 * bleed through the fill.
 */
export const BEHIND_PAINT = "var(--pc-behind, #A4A4A4)";
export const GHOST_OPACITY = 0.34;
/**
 * The occlusion layer: a paper-colored silhouette (all subpaths, nonzero
 * fill) PLUS a casing stroke of the same paint, drawn under a symbol's
 * strokes so a figure in front of a cork board hides the board behind it
 * instead of reading as a translucent ghost. Fill alone leaks where the CAD
 * contours are open polylines (hair, hands); the casing blots those dense
 * regions and adds the halo that separates overlapping figures. Exterior
 * context symbols (ext.*) skip it — a cloud's chord-fill would erase the
 * sky behind it.
 */
export const OCCLUSION_PAINT = "var(--pc-grid-bg, #F2F2F2)";
/**
 * THE SHELL RESTYLE (ADR 0015, step 3): the section's paper-and-wash look
 * is tokens on generated code, never a bitmap. Cut construction (slabs,
 * parapets) fills solid in POCHE_PAINT; every room volume in the plane
 * carries one WASH_PAINT rectangle at WASH_OPACITY under its contents, so
 * a glazed floor reads as a lit interior against the paper. The literal
 * fallbacks match apps/web/src/design/tokens.css.
 */
export const POCHE_PAINT = "var(--pc-poche, #262626)";
export const WASH_PAINT = "var(--pc-wash, #D6D6D6)";
export const WASH_OPACITY = 0.32;
export const OCCLUSION_CASING = 3;
const CmSchema = z.number().finite();
/**
 * THE MIXED REPRESENTATION (ADR 0015). A symbol may be PLATE-BACKED: its
 * markup is one `<image>` of a transparent PNG drawn in the illustrated
 * style (Ink Stroke, monochrome; ADR 0017), sized in cm exactly like a vector
 * symbol, so the composer places, mirrors, orders and tones it through the
 * same `<use>` with zero special cases. The shell stays parametric line
 * work; only symbols may be plates. The PNG's alpha IS the occlusion layer.
 * Committed scale: PLATE_PX_PER_CM pixels per cm at 1:50, so a 175 cm figure
 * is 700 px tall in the pack — the section draws it at one to three px/cm,
 * so that is crisp at every zoom it reaches. Deliveries arrive larger (a
 * 1024×1536 render puts a figure near 8 px/cm) and the ingest resamples
 * them down with an alpha-correct area average.
 */
export const PLATE_PX_PER_CM = 4;
/** One plate may not exceed this many bytes. Plates ship as static files (ADR 0015 amendment), so this bounds one request, not the bundle. */
export const PLATE_MAX_BYTES = 1_000_000;
/** Nor this many pixels on either side. */
export const PLATE_MAX_PX = 2048;
/** The behind tone for a plate: a filter mapping ink to the behind paint and white to paper (literal, filters cannot read CSS variables). */
export const BEHIND_PAINT_HEX = "#A4A4A4";
export const PAPER_HEX = "#F2F2F2";
export const PLATE_BEHIND_FILTER_ID = "pc-plate-behind";
export const PlateSchema = z.strictObject({
    /** Path to the transparent PNG, relative to the pack dir, under plates/. */
    file: z.string().regex(/^plates\/[a-z0-9.-]+\.png$/),
    /** SHA-256 of the committed bytes; an art receipt's artifact_sha256 must equal it. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** Pixels per cm of the delivered plate; width/height must equal footprint × pxPerCm within a pixel. */
    pxPerCm: z.number().positive().finite(),
    width: z.number().int().positive().max(PLATE_MAX_PX),
    height: z.number().int().positive().max(PLATE_MAX_PX),
    /**
     * True when the plate was FITTED to an authored footprint (a vector symbol
     * replaced in place): the subject sits bottom-centre inside the box and
     * the alpha bounds may be inset from the sides and top, never the bottom.
     * Stations, slots and anchors keep meaning exactly what they meant.
     */
    fitted: z.boolean().optional(),
    /** A clipped shared painting, rather than an isolated RGBA delivery. */
    registration: RasterRegistrationSchema.optional(),
});
const AssetSlotSchema = z.strictObject({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    /** cm, relative to the symbol's own viewBox origin. Must lie inside the footprint. */
    x: CmSchema,
    y: CmSchema,
});
export const AssetSymbolSchema = z.strictObject({
    /** Namespaced, globally unique across all installed packs, e.g. "core.fig.stand". */
    id: SymbolRefSchema,
    /** Path to the SVG file containing exactly one matching <symbol>, relative to the pack dir. */
    file: z.string().min(1),
    /** Tight bounding box size in cm; must equal the file's viewBox width/height. */
    footprint: z.strictObject({ w: CmSchema.positive(), h: CmSchema.positive() }),
    /** Ground/placement point in the symbol's own coordinates (usually bottom-centre). */
    anchor: z.strictObject({ x: CmSchema, y: CmSchema }),
    /** Depth band for draw order within a room. */
    zband: z.enum(["back", "mid", "front"]),
    /** Attachment points other symbols can occupy (a chair's seat, a desk's surface). */
    slots: z.array(AssetSlotSchema),
    /**
     * data-part group names present in the file — the animation hooks.
     * Figures MUST be articulated: a "fig." or "cast." symbol with no parts
     * is not contract-complete; assets:validate enforces this.
     */
    parts: z.array(z.string().regex(/^[a-z][a-z0-9-]*$/)),
    tags: z.array(z.string().regex(/^[a-z][a-z0-9-]*$/)),
    /**
     * Licence key into the pack's LICENSES.md. Every symbol MUST have one;
     * a symbol with no licence line fails the build (ADR 0009: no
     * unattributable symbol ships).
     */
    license: z.string().min(1),
    /** Present when the symbol is plate-backed (ADR 0015); absent for vector symbols. */
    plate: PlateSchema.optional(),
    illustration: IllustrationMetadataSchema.optional(),
});
export const AssetPackManifestSchema = z.strictObject({
    /** The namespace prefix of every symbol id in this pack ("core", "hvg"). */
    packId: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** Declared unit of the ORIGINAL source material, recorded for provenance. */
    sourceUnit: z.enum(["cm", "mm", "m", "pt", "px", "unknown"]),
    symbols: z.array(AssetSymbolSchema).min(1),
    visualProfiles: z.array(VisualProfileSchema).max(8).optional(),
});
//# sourceMappingURL=asset-pack.js.map